// Shared UI primitives — buttons, cards, selects, multi-select, chips, etc.

import { useState, useEffect, useRef } from 'react';
import { createIcons, icons } from 'lucide';

const css = (...parts) => parts.filter(Boolean).join(' ');

// Inject reusable styles once
(function injectStyles() {
  if (document.getElementById('app-ui-styles')) return;
  const s = document.createElement('style');
  s.id = 'app-ui-styles';
  s.textContent = `
    /* Layout */
    .app { display: grid; grid-template-columns: 240px 1fr; grid-template-rows: minmax(0, 1fr); height: 100vh; background: var(--bg); }
    .sidebar { background: var(--surface); border-right: 1px solid var(--border); display: flex; flex-direction: column; min-height: 0; }
    .sidebar-head { padding: 18px 18px 14px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid var(--border); }
    .brand-mark { width: 32px; height: 32px; flex-shrink: 0; }
    .brand-name { font-weight: 600; letter-spacing: -0.01em; font-size: 15px; }
    .brand-sub { font-size: 11px; color: var(--ink-3); margin-top: 1px; letter-spacing: 0.02em; text-transform: uppercase; }
    .nav { padding: 12px 8px; flex: 1; overflow-y: auto; }
    .nav-section { font-size: 11px; color: var(--ink-3); text-transform: uppercase; letter-spacing: 0.06em; padding: 12px 10px 6px; }
    .nav-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 6px; color: var(--ink-2); cursor: pointer; font-size: 13px; user-select: none; }
    .nav-item:hover { background: var(--surface-hover); color: var(--ink); }
    .nav-item.active { background: var(--teal-tint); color: var(--teal-2); font-weight: 500; }
    html.dark .nav-item.active { color: var(--teal-2); }
    .nav-tag { margin-left: auto; color: var(--ink-3); font-size: 11px; }
    /* Audience segmented control */
    .aud-seg { display: flex; gap: 4px; padding: 4px 8px 4px; }
    .aud-seg-btn { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 8px 4px; border-radius: 7px; border: 1px solid var(--border); background: var(--surface); color: var(--ink-2); cursor: pointer; font-size: 11px; transition: background .12s, border-color .12s, color .12s; }
    .aud-seg-btn:hover:not(:disabled) { background: var(--surface-hover); color: var(--ink); }
    .aud-seg-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .aud-seg-btn.active { background: var(--teal-tint); border-color: var(--teal); color: var(--teal-2); font-weight: 500; }
    .aud-seg-count { font-family: 'Geist Mono', monospace; font-size: 10px; opacity: 0.75; }
    .sidebar-foot { padding: 12px; border-top: 1px solid var(--border); display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--ink-3); }

    .main { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
    .topbar { height: 56px; flex-shrink: 0; background: var(--surface); border-bottom: 1px solid var(--border); display: flex; align-items: center; padding: 0 20px; gap: 12px; position: sticky; top: 0; z-index: 5; }
    .topbar-title { font-weight: 600; letter-spacing: -0.01em; font-size: 15px; }
    .topbar-spacer { flex: 1; }

    .filters-bar { background: var(--surface); border-bottom: 1px solid var(--border); padding: 12px 20px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .content { flex: 1; overflow-y: auto; padding: 20px; min-height: 0; }
    .content-inner { max-width: 1400px; margin: 0 auto; }

    /* Buttons */
    .btn { font: inherit; font-size: 13px; padding: 7px 12px; border-radius: 7px; border: 1px solid var(--border-strong); background: var(--surface); color: var(--ink); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; line-height: 1; transition: background .12s, border-color .12s; }
    .btn:hover { background: var(--surface-hover); }
    .btn-primary { background: var(--teal); color: #fff; border-color: var(--teal); }
    .btn-primary:hover { background: var(--teal-2); border-color: var(--teal-2); }
    .btn-ghost { background: transparent; border-color: transparent; color: var(--ink-2); }
    .btn-ghost:hover { background: var(--surface-hover); color: var(--ink); }
    .btn-icon { padding: 7px; }
    .btn-sm { padding: 5px 10px; font-size: 12px; }

    /* Inputs */
    .input, .select { font: inherit; font-size: 13px; padding: 7px 10px; border-radius: 7px; border: 1px solid var(--border-strong); background: var(--surface); color: var(--ink); outline: none; }
    .input:focus, .select:focus { border-color: var(--teal); box-shadow: 0 0 0 3px var(--teal-tint); }

    /* Cards */
    .card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); }
    .card-head { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
    .card-title { font-weight: 600; font-size: 14px; letter-spacing: -0.005em; }
    .card-sub { font-size: 12px; color: var(--ink-3); margin-top: 2px; }
    .card-body { padding: 20px; }

    /* Stat */
    .stat { padding: 18px 20px; }
    .stat-label { font-size: 12px; color: var(--ink-3); display: flex; align-items: center; gap: 6px; }
    .stat-value { font-size: 32px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.05; margin-top: 6px; }
    .stat-delta { font-size: 12px; margin-top: 6px; color: var(--ink-3); display: flex; align-items: center; gap: 4px; }

    /* Chips */
    .chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px; border-radius: 999px; font-size: 11px; font-weight: 500; background: var(--surface-2); color: var(--ink-2); border: 1px solid var(--border); }
    .chip-teal { background: var(--teal-tint); color: var(--teal-2); border-color: transparent; }
    .chip-green { background: color-mix(in srgb, var(--green) 14%, transparent); color: var(--green); border-color: transparent; }
    .chip-red { background: color-mix(in srgb, var(--red) 14%, transparent); color: var(--red); border-color: transparent; }
    .chip-amber { background: color-mix(in srgb, var(--amber) 14%, transparent); color: var(--amber); border-color: transparent; }

    /* Tables */
    table.dt { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
    table.dt th { text-align: left; font-weight: 500; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-3); padding: 10px 14px; border-bottom: 1px solid var(--border); background: var(--surface); position: sticky; top: 0; }
    table.dt td { padding: 11px 14px; border-bottom: 1px solid var(--border); color: var(--ink-2); vertical-align: middle; }
    table.dt tr:last-child td { border-bottom: none; }
    table.dt tr:hover td { background: var(--surface-hover); }
    table.dt td.num { font-family: 'Geist Mono', monospace; font-size: 12px; color: var(--ink); text-align: right; font-variant-numeric: tabular-nums; }

    /* Multi-select dropdown */
    .ms-wrap { position: relative; }
    .ms-trigger { font-size: 13px; padding: 7px 28px 7px 10px; border-radius: 7px; border: 1px solid var(--border-strong); background: var(--surface); color: var(--ink); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; min-width: 140px; position: relative; }
    .ms-trigger:hover { background: var(--surface-hover); }
    .ms-caret { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: var(--ink-3); }
    .ms-panel { position: absolute; top: calc(100% + 4px); left: 0; min-width: 240px; max-height: 320px; overflow-y: auto; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; box-shadow: var(--shadow); z-index: 50; padding: 4px; }
    .ms-item { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 5px; cursor: pointer; font-size: 13px; }
    .ms-item:hover { background: var(--surface-hover); }
    .ms-checkbox { width: 14px; height: 14px; border-radius: 3px; border: 1px solid var(--border-strong); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; background: var(--surface); }
    .ms-checkbox.on { background: var(--teal); border-color: var(--teal); color: #fff; }
    .ms-search { padding: 6px 8px; border-bottom: 1px solid var(--border); margin-bottom: 4px; }
    .ms-search input { width: 100%; border: none; outline: none; font: inherit; font-size: 13px; background: transparent; color: var(--ink); }

    /* Tabs */
    .tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--border); padding: 0 20px; background: var(--surface); }
    .tab { padding: 12px 14px; font-size: 13px; color: var(--ink-3); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; user-select: none; display: inline-flex; align-items: center; gap: 6px; }
    .tab:hover { color: var(--ink); }
    .tab.active { color: var(--ink); border-bottom-color: var(--teal); font-weight: 500; }

    /* Drop zone */
    .dropzone { border: 2px dashed var(--border-strong); border-radius: var(--radius-lg); padding: 56px 24px; text-align: center; background: var(--surface); transition: background .15s, border-color .15s; }
    .dropzone.drag { border-color: var(--teal); background: var(--teal-tint); }
    .dropzone-title { font-size: 18px; font-weight: 600; letter-spacing: -0.01em; }
    .dropzone-sub { font-size: 13px; color: var(--ink-3); margin-top: 6px; }
    .dropzone-icon { width: 44px; height: 44px; border-radius: 12px; background: var(--teal-tint); color: var(--teal-2); display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px; }
    .dropzone-icon [data-lucide] { width: 22px; height: 22px; }

    /* Skeleton */
    @keyframes shim { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
    .skel { background: linear-gradient(90deg, var(--surface-2) 0%, var(--border) 50%, var(--surface-2) 100%); background-size: 800px 100%; animation: shim 1.4s infinite; border-radius: 6px; }

    /* Spinner */
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner { width: 28px; height: 28px; border-radius: 50%; border: 3px solid var(--border); border-top-color: var(--teal); animation: spin .7s linear infinite; }

    /* Empty state */
    .empty { text-align: center; padding: 60px 20px; color: var(--ink-3); }
    .empty-icon { width: 56px; height: 56px; margin: 0 auto 14px; border-radius: 14px; background: var(--surface-2); color: var(--ink-3); display: inline-flex; align-items: center; justify-content: center; }
    .empty-icon [data-lucide] { width: 28px; height: 28px; }
    .empty-title { font-size: 16px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
    .empty-sub { font-size: 13px; color: var(--ink-3); max-width: 360px; margin: 0 auto; }

    /* Mobile */
    @media (max-width: 720px) {
      .app { grid-template-columns: 1fr; grid-template-rows: minmax(0, 1fr) 56px; }
      .sidebar { display: none; }
      .mobile-nav { display: flex; }
    }
    .mobile-nav { display: none; background: var(--surface); border-top: 1px solid var(--border); justify-content: space-around; align-items: stretch; }
    .mobile-nav-item { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; font-size: 10px; color: var(--ink-3); cursor: pointer; padding: 6px; }
    .mobile-nav-item.active { color: var(--teal); }
    .mobile-nav-item [data-lucide] { width: 18px; height: 18px; }
    @media (max-width: 720px) {
      .filters-bar { padding: 10px 12px; }
      .content { padding: 12px; }
      .grid-stats { grid-template-columns: 1fr 1fr !important; }
      .grid-row { grid-template-columns: 1fr !important; }
      .topbar { padding: 0 12px; }
    }
  `;
  document.head.appendChild(s);
})();

// Lucide icon. lucide's createIcons() REPLACES the [data-lucide] node with a
// fresh <svg>, detaching the original. If React owns that node it later calls
// removeChild on a node lucide already removed → "NotFoundError: The object can
// not be found here" (and a cascading invalid-hook-call) on unmount. So React
// only renders the wrapper <span>; the replaceable node is created imperatively
// inside it, where React never tracks (and never tries to remove) it.
function Icon({ name, size, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.replaceChildren();
    const el = document.createElement('i');
    el.setAttribute('data-lucide', name);
    if (size != null) {
      const px = typeof size === 'number' ? `${size}px` : size;
      el.style.width = px;
      el.style.height = px;
    }
    host.appendChild(el);
    createIcons({ nameAttr: 'data-lucide', icons });
    return () => host.replaceChildren();
  }, [name, size]);
  return <span ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...(style || {}) }} />;
}

function StatCard({ label, value, delta, deltaTone, icon }) {
  return (
    <div className="card stat">
      <div className="stat-label">{icon && <Icon name={icon} />} {label}</div>
      <div className="stat-value">{value}</div>
      {delta && <div className="stat-delta" style={deltaTone === 'pos' ? { color: 'var(--green)' } : deltaTone === 'neg' ? { color: 'var(--red)' } : null}>{delta}</div>}
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const allOn = selected.size === 0 || selected.size === options.length;
  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()));
  function toggle(opt) {
    const s = new Set(selected);
    if (s.has(opt)) s.delete(opt); else s.add(opt);
    onChange(s);
  }
  function clear() { onChange(new Set()); }
  const text = selected.size === 0 ? `All ${label.toLowerCase()}` : selected.size === 1 ? [...selected][0] : `${selected.size} ${label.toLowerCase()}`;
  return (
    <div className="ms-wrap" ref={ref}>
      <button className="ms-trigger" onClick={() => setOpen(o => !o)}>
        <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{label}:</span>
        <span style={{ fontWeight: 500 }}>{text}</span>
        <Icon name="chevron-down" />
      </button>
      {open && (
        <div className="ms-panel">
          {options.length > 6 && (
            <div className="ms-search"><input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} /></div>
          )}
          <div className="ms-item" onClick={clear} style={{ color: 'var(--ink-3)', justifyContent: 'space-between' }}>
            <span>Clear all</span>
            {allOn && <span style={{ fontSize: 11 }}>✓ all shown</span>}
          </div>
          {filtered.map(o => (
            <div key={o} className="ms-item" onClick={() => toggle(o)}>
              <span className={css('ms-checkbox', selected.has(o) && 'on')}>
                {selected.has(o) && <Icon name="check" size={10} />}
              </span>
              <span style={{ flex: 1 }}>{o}</span>
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: 12, color: 'var(--ink-3)', fontSize: 12 }}>No matches</div>}
        </div>
      )}
    </div>
  );
}

function Empty({ icon, title, sub, action }) {
  return (
    <div className="empty">
      <div className="empty-icon"><Icon name={icon || 'inbox'} /></div>
      <div className="empty-title">{title}</div>
      <div className="empty-sub">{sub}</div>
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

// Shared full-area loading + error states used by the analytics views and the
// catalogue/contracts/app data gates.
function Loading({ label }) {
  return (
    <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-3)' }}>
      <div className="spinner" style={{ margin: '0 auto 14px' }} />
      <div style={{ fontSize: 13 }}>{label || 'Loading…'}</div>
    </div>
  );
}

function LoadError({ error, title, onRetry }) {
  return (
    <Empty
      icon="alert-triangle"
      title={title || "Couldn't load this view"}
      sub={error}
      action={onRetry ? <button className="btn btn-sm" onClick={onRetry}>Retry</button> : null}
    />
  );
}

function fmtNum(n) { return (n || 0).toLocaleString(); }

export { Icon, StatCard, MultiSelect, Empty, Loading, LoadError, css, fmtNum };
